import { createSlice } from '@reduxjs/toolkit';
import { get, post } from 'api/fetch';
import { initialize } from 'state/app';
import { decodeJWT, createJWT } from 'auth';
import history from 'routing/history';
import { fireLoginTrackingEvent } from 'analytics';

// ================== Reducers ================== //

const slice = createSlice({
  name: 'nomad_insurance/planholder',
  initialState: {
    isLoading: false,
    error: null,
    planholder: null,

    facebookLogin: {
      isLoading: false,
      error: false,
      user: null
    }
  },
  reducers: {
    // fetchPlanholderInfo
    fetchPlanholderInfoRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchPlanholderInfoSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.planholder = payload;
    },
    fetchPlanholderInfoFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
      state.planholder = null;
    },
    // setPlanHolderInfo
    setPlanholderInfoRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    setPlanholderInfoSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.planholder = payload;
    },
    setPlanholderInfoFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    // updatePlanholderInfo
    updatePlanholderInfo: (state, { payload }) => {
      state.planholder = {
        ...state.planholder,
        ...payload
      };
    },
    // updateUserDependantInfo
    updatePlanholderDependantInfo: (state, { payload }) => {
      const { index, info } = payload;

      state.planholder.dependants[index] = {
        ...state.planholder.dependants[index],
        ...info
      };
    },
    // facebookLogin
    facebookLoginRequest: (state) => {
      state.facebookLogin.isLoading = true;
      state.facebookLogin.error = null;
    },
    facebookLoginSuccess: (state, { payload }) => {
      state.facebookLogin.isLoading = false;
      state.facebookLogin.error = null;
      state.facebookLogin.user = payload;
    },
    facebookLoginFailure: (state, { payload }) => {
      state.facebookLogin.isLoading = false;
      state.facebookLogin.error = payload;
      state.facebookLogin.user = null;
    }
  }
});

export default slice.reducer;

// ================== Actions ================== //

const { actions } = slice;

export const fetchPlanholderInfo = (redirectOnUnauthorized) => async (dispatch) => {
  dispatch(actions.fetchPlanholderInfoRequest());

  const accessToken = decodeJWT('accessToken');

  if (!accessToken)
    throw new Error('No tokens present with which user info can be retrieved');

  try {
    const planholder = await get('/plan-holders/v1', {
      isNIOrchestrator: true,
      redirectOnUnauthorized
    });

    if (planholder.userID !== decodeJWT('userID'))
      throw new Error('UserInfo id and accessToken id do not match');

    dispatch(actions.fetchPlanholderInfoSuccess(planholder));
  } catch (error) {
    dispatch(actions.fetchPlanholderInfoFailure(error));
  }
};

export const setPlanHolderInfo = (info, onSuccess) => async (dispatch) => {
  dispatch(actions.setPlanholderInfoRequest());

  try {
    const planholder = await post('/plan-holders/v1', info, {
      isNIOrchestrator: true
    });

    dispatch(actions.setPlanholderInfoSuccess(planholder));

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    dispatch(actions.setPlanholderInfoFailure(error));
  }
};

export const updatePlanholderInfo = (info) => (dispatch) => {
  dispatch(actions.updatePlanholderInfo(info));
};

export const updatePlanholderDependantInfo = (index, info) => (dispatch) => {
  dispatch(actions.updatePlanholderDependantInfo({ index, info }));
};

export const facebookLogin = (facebookResponse) => async (dispatch, getState) => {
  if (!facebookResponse.userID) {
    return;
  }

  dispatch(actions.facebookLoginRequest());

  const body = {
    facebookID: facebookResponse?.userID || null,
    accessToken: facebookResponse?.accessToken || null,
    profilePictureUrl: facebookResponse?.picture?.data?.url || null,
    firstName: facebookResponse.name ? facebookResponse.name.split(' ')[0] : null,
    lastName: facebookResponse.name ? facebookResponse.name.split(' ')[1] : null,
    email: facebookResponse.email ? facebookResponse.email : null
  };

  try {
    const response = await post(
      '/v1/api/auth/sign-in/facebook',
      { token: facebookResponse.accessToken },
      { isUserService: true }
    );
    await createJWT({
      userID: response.userId,
      accessToken: response.token
    });
    await dispatch(initialize(true, 'nomad_insurance'));
    dispatch(actions.facebookLoginSuccess(body));

    fireLoginTrackingEvent(response.userId, 'facebook');

    const { hasCompletedProfile } = getState().nomadInsurance.planholder.planholder;

    history.push(
      hasCompletedProfile ? '/nomad-insurance/profile' : '/nomad-insurance/registration'
    );
  } catch (error) {
    dispatch(actions.facebookLoginFailure(error));
  }
};

export const dismissNotification = (notificationID) => async (dispatch, getState) => {
  try {
    await post('/insurance/ni/plan-holders/notification/dismiss', { notificationID });
    const { notifications } = getState().nomadInsurance.planholder.planholder;
    dispatch(
      actions.updatePlanholderInfo(notifications.filter((x) => x.id !== notificationID))
    );
  } catch (error) {
    // TODO handle this error
    throw Error(error);
  }
};
